import React, { useEffect, useRef, useState } from 'react'
import MapboxDraw from '@mapbox/mapbox-gl-draw'
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css'
import type { FeatureCollection } from 'geojson'
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import ReactDOMServer from 'react-dom/server'

import { MAPBOX_TOKEN } from '~config'
import { type GeoPoint } from '~types'

import {
  type ExtendedGeoPoint,
  type GroupedPointsConfig,
  removeClusteredPointsFromMap,
  removeGroupedClusteredPointsFromMap,
  showMultiCasePopup,
  updateClusteredPointsOnMap,
  updateGroupedClusteredPointsOnMap,
} from './MapBoxClusteringUtils'

const DEFAULT_POSITION: GeoPoint = {
  lon: -122.4194,
  lat: 37.7749,
}

const EMPTY_MAP_FLY_DURATION_MS = 1500

interface MapBoxWithControlsProps {
  id: string
  initialPosition?: GeoPoint
  enableGeolocation?: boolean
  enableSatellite?: boolean
  enableMultipleSelection?: boolean
  onDrawComplete?: (features: FeatureCollection) => void
  showZoomControls?: boolean
  showDrawControls?: boolean
  width?: string | number
  height?: string | number
  pointsToDraw?: ExtendedGeoPoint[]
  fitMapToPoints?: boolean
  groupedPointsToDraw?: GroupedPointsConfig
  onMapReady?: (map: mapboxgl.Map) => void
}

const MapBoxWithControls: React.FC<MapBoxWithControlsProps> = ({
  id,
  initialPosition = DEFAULT_POSITION,
  enableGeolocation = false,
  enableSatellite = false,
  enableMultipleSelection = true,
  onDrawComplete,
  showZoomControls = false,
  showDrawControls = false,
  width = '100%',
  height = '100%',
  pointsToDraw = [],
  fitMapToPoints = true,
  groupedPointsToDraw,
  onMapReady,
}) => {
  const [viewState, _setViewState] = useState({
    longitude: initialPosition.lon,
    latitude: initialPosition.lat,
    zoom: 12,
  })
  const [mapStyle, setMapStyle] = useState('mapbox://styles/mapbox/streets-v11')
  const drawRef = useRef<MapboxDraw | null>(null)
  const mapRef = useRef<mapboxgl.Map | null>(null)
  const mapContainerRef = useRef<HTMLDivElement | null>(null)
  const geolocateRef = useRef<mapboxgl.GeolocateControl | null>(null)
  const [mapLoaded, setMapLoaded] = useState(false)
  const tooltipRef = useRef<mapboxgl.Popup | null>(null)
  const prevInitialPositionRef = useRef<GeoPoint | undefined>(initialPosition)
  const onMapReadyRef = useRef(onMapReady)
  useEffect(() => {
    onMapReadyRef.current = onMapReady
  }, [onMapReady])

  const containerStyle = {
    width,
    height: height,
    flexGrow: 1,
  }

  const addBuildings = (map: mapboxgl.Map): void => {
    const style = map.getStyle()
    if (style?.name !== 'Mapbox Satellite') {
      map.addLayer({
        id: '3d-buildings',
        source: 'composite',
        'source-layer': 'building',
        filter: ['==', 'extrude', 'true'],
        type: 'fill-extrusion',
        minzoom: 15,
        paint: {
          'fill-extrusion-color': '#aaa',
          'fill-extrusion-height': [
            'interpolate',
            ['linear'],
            ['zoom'],
            15,
            0,
            15.05,
            ['get', 'height'],
          ],
          'fill-extrusion-base': [
            'interpolate',
            ['linear'],
            ['zoom'],
            15,
            0,
            15.05,
            ['get', 'min_height'],
          ],
          'fill-extrusion-opacity': 0.6,
        },
      })
    }
  }

  const removeBuildings = (map: mapboxgl.Map): void => {
    const style = map.getStyle()
    if (style?.name !== 'Mapbox Satellite' && map.getLayer('3d-buildings')) {
      map.removeLayer('3d-buildings')
    }
  }

  useEffect(() => {
    if (mapContainerRef.current && !mapRef.current) {
      if (!MAPBOX_TOKEN) {
        throw new Error('MAPBOX_TOKEN is not defined')
      }
      mapboxgl.accessToken = MAPBOX_TOKEN
      mapRef.current = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: mapStyle,
        center: [initialPosition.lon, initialPosition.lat],
        zoom: viewState.zoom,
        accessToken: MAPBOX_TOKEN,
      })

      mapRef.current.on('load', () => {
        setMapLoaded(true)
        onMapReadyRef.current?.(mapRef.current!)

        if (showDrawControls) {
          drawRef.current = new MapboxDraw({
            displayControlsDefault: false,
            controls: {
              polygon: true,
              trash: true,
            },
          })
          mapRef.current?.addControl(drawRef.current)

          tooltipRef.current = new mapboxgl.Popup({
            closeButton: false,
            closeOnClick: false,
            className: 'draw-tooltip',
          })

          // Create handlers outside of the load event to avoid recreating them
          const mouseEnterHandler = (e: Event): void => {
            const drawButton = e.target as HTMLElement
            tooltipRef.current
              ?.setHTML('Draw a polygon')
              .setLngLat(
                mapRef.current?.unproject([
                  drawButton.getBoundingClientRect().left +
                    drawButton.getBoundingClientRect().width / 2,
                  drawButton.getBoundingClientRect().top +
                    drawButton.getBoundingClientRect().height,
                ]) || [0, 0],
              )
              .addTo(mapRef.current!)
          }

          const mouseLeaveHandler = (): void => {
            tooltipRef.current?.remove()
          }

          // Attach listeners immediately after adding the control
          const drawButton = document.querySelector(
            '.mapbox-gl-draw_ctrl-draw-btn.mapbox-gl-draw_polygon',
          )
          if (drawButton) {
            drawButton.addEventListener('mouseenter', mouseEnterHandler)
            drawButton.addEventListener('mouseleave', mouseLeaveHandler)
          }
        }

        if (showZoomControls) {
          mapRef.current?.addControl(
            new mapboxgl.NavigationControl({
              showCompass: false,
            }),
            'top-left',
          )
        }

        if (enableGeolocation) {
          geolocateRef.current = new mapboxgl.GeolocateControl({
            positionOptions: {
              enableHighAccuracy: true,
            },
            trackUserLocation: true,
          })
          mapRef.current?.addControl(geolocateRef.current)
        }

        class LayerToggleControl {
          _map: mapboxgl.Map | null = null
          _container: HTMLDivElement | null = null
          _button: HTMLButtonElement | null = null
          _iconContainer: HTMLDivElement | null = null

          onAdd(map: mapboxgl.Map): HTMLDivElement {
            this._map = map
            let _this = this

            this._button = document.createElement('button')
            this._button.className = 'mapboxgl-ctrl-icon mapboxgl-ctrl-satellite'
            this._button.type = 'button'
            this._button.title = 'Toggle Satellite'
            this._button.onclick = function (): void {
              var style = map.getStyle()
              if (style?.name === 'Mapbox Satellite') {
                map.setStyle('mapbox://styles/mapbox/streets-v11')
                if (_this._button) {
                  _this._button.className = 'mapboxgl-ctrl-icon mapboxgl-ctrl-satellite'
                }
              } else {
                map.setStyle('mapbox://styles/mapbox/satellite-v9')
                if (_this._button) {
                  _this._button.className = 'mapboxgl-ctrl-icon mapboxgl-ctrl-street'
                }
              }
            }

            this._container = document.createElement('div')
            this._container.className = 'mapboxgl-ctrl-group mapboxgl-ctrl'
            this._container.appendChild(this._button)

            return this._container
          }

          onRemove(): void {
            this._container?.parentNode?.removeChild(this._container)
            this._map = null
          }
        }

        class Layer3DControl {
          _map: mapboxgl.Map | null = null
          _container: HTMLDivElement | null = null
          _button: HTMLButtonElement | null = null

          onAdd(map: mapboxgl.Map): HTMLDivElement {
            this._map = map
            let _this = this

            this._button = document.createElement('button')
            this._button.className = 'mapboxgl-ctrl-icon mapboxgl-ctrl-3d'
            this._button.type = 'button'
            this._button.title = 'Toggle 3D'
            this._button.onclick = function (): void {
              if (map.getPitch() === 0) {
                map.easeTo({ pitch: 70, bearing: -20 })
                if (_this._button) {
                  _this._button.className = 'mapboxgl-ctrl-icon mapboxgl-ctrl-2d'
                }
                addBuildings(map)
              } else {
                map.easeTo({ pitch: 0, bearing: 0 })
                if (_this._button) {
                  _this._button.className = 'mapboxgl-ctrl-icon mapboxgl-ctrl-3d'
                }
                removeBuildings(map)
              }
            }

            this._container = document.createElement('div')
            this._container.className = 'mapboxgl-ctrl-group mapboxgl-ctrl'
            this._container.appendChild(this._button)

            return this._container
          }
          onRemove(): void {
            this._container?.parentNode?.removeChild(this._container)
            this._map = null
          }
        }

        mapRef.current?.addControl(new Layer3DControl(), 'top-left')
        mapRef.current?.addControl(new LayerToggleControl(), 'top-left')

        mapRef.current?.on('draw.create', handleDrawEvent)
        mapRef.current?.on('draw.update', handleDrawEvent)
        mapRef.current?.on('draw.delete', handleDrawEvent)
      })
    }

    return (): void => {
      if (mapRef.current) {
        const drawButton = document.querySelector(
          '.mapbox-gl-draw_ctrl-draw-btn.mapbox-gl-draw_polygon',
        )
        if (drawButton) {
          const mouseEnterHandler = (): void => {}
          const mouseLeaveHandler = (): void => {}
          drawButton.removeEventListener('mouseenter', mouseEnterHandler)
          drawButton.removeEventListener('mouseleave', mouseLeaveHandler)
        }
        mapRef.current.remove()
        mapRef.current = null
        setMapLoaded(false)
      }
    }
  }, [id, mapStyle, viewState.zoom, showDrawControls, showZoomControls, enableGeolocation])

  // Effect for updating clustered points on the map (single-source mode)
  useEffect(() => {
    if (groupedPointsToDraw) return
    const sourceId = 'clustered-points'
    if (!mapRef.current || !mapLoaded) return

    const map = mapRef.current
    const styleLoadHandler = (): void => {
      updateClusteredPointsOnMap(map, pointsToDraw, fitMapToPoints, sourceId)
    }
    map.on('style.load', styleLoadHandler)

    updateClusteredPointsOnMap(map, pointsToDraw, fitMapToPoints, sourceId)

    return (): void => {
      try {
        map.off('style.load', styleLoadHandler)
        removeClusteredPointsFromMap(map, sourceId)
      } catch {}
    }
  }, [pointsToDraw, fitMapToPoints, mapLoaded, groupedPointsToDraw])

  // Effect for updating grouped clustered points on the map (per-status-group mode)
  useEffect(() => {
    if (!groupedPointsToDraw) return
    if (!mapRef.current || !mapLoaded) return

    const map = mapRef.current
    const groupKeys = Object.keys(groupedPointsToDraw)

    const styleLoadHandler = (): void => {
      updateGroupedClusteredPointsOnMap(map, groupedPointsToDraw, fitMapToPoints)
    }
    map.on('style.load', styleLoadHandler)

    updateGroupedClusteredPointsOnMap(map, groupedPointsToDraw, fitMapToPoints)

    return (): void => {
      try {
        map.off('style.load', styleLoadHandler)
        removeGroupedClusteredPointsFromMap(map, groupKeys)
      } catch {}
    }
  }, [groupedPointsToDraw, fitMapToPoints, mapLoaded])

  useEffect(() => {
    const container = mapContainerRef.current
    const map = mapRef.current
    if (!container || !map || !mapLoaded) return
    const observer = new ResizeObserver(() => {
      map.resize()
    })
    observer.observe(container)
    return (): void => {
      observer.disconnect()
    }
  }, [mapLoaded])

  const hasPointsToFit =
    pointsToDraw.length > 0 ||
    Object.values(groupedPointsToDraw ?? {}).some((group) => group.points.length > 0)

  useEffect(() => {
    if (!fitMapToPoints || hasPointsToFit) return
    if (!mapRef.current || !mapLoaded) return
    mapRef.current.flyTo({
      center: [initialPosition.lon, initialPosition.lat],
      zoom: viewState.zoom,
      duration: EMPTY_MAP_FLY_DURATION_MS,
      essential: true,
    })
  }, [initialPosition, mapLoaded, fitMapToPoints, hasPointsToFit, viewState.zoom])

  // Effect for updating map center point
  useEffect(() => {
    if (fitMapToPoints) return
    if (!mapRef.current || !initialPosition || !mapLoaded) return
    const prev = prevInitialPositionRef.current
    if (prev && prev.lon === initialPosition.lon && prev.lat === initialPosition.lat) return
    prevInitialPositionRef.current = initialPosition
    mapRef.current.flyTo({
      center: [initialPosition.lon, initialPosition.lat],
      duration: 5000,
      essential: true,
    })
  }, [initialPosition, mapLoaded, fitMapToPoints])

  const handleDrawEvent = (): void => {
    if (drawRef.current && onDrawComplete) {
      const features = drawRef.current.getAll()

      if (!enableMultipleSelection) {
        if (features.features.length > 1) {
          const latestFeature = features.features[features.features.length - 1]
          drawRef.current.deleteAll()
          drawRef.current.add(latestFeature)
          features.features = [latestFeature]
        }
      }

      onDrawComplete(features)
    }
  }

  useEffect(() => {
    if (enableSatellite) {
      setMapStyle('mapbox://styles/mapbox/satellite-v9')
    } else {
      setMapStyle('mapbox://styles/mapbox/streets-v11')
    }
  }, [enableSatellite])

  useEffect(() => {
    if (!mapRef.current || !mapLoaded) return

    const map = mapRef.current

    pointsToDraw.forEach((point) => {
      const el = document.createElement('div')
      el.className = 'marker'

      new mapboxgl.Marker(el).setLngLat([point.lon, point.lat]).addTo(map)

      if (point.popupContent !== null && point.popupContent !== undefined) {
        el.addEventListener('click', () => {
          const popupContentString = ReactDOMServer.renderToString(<>{point.popupContent}</>)
          if (
            popupContentString.includes('Multiple Cases') ||
            popupContentString.includes('Cases at this Location')
          ) {
            showMultiCasePopup(map, [point], [point.lon, point.lat])
          } else {
            new mapboxgl.Popup({
              className: popupContentString.includes('Accordion') ? 'cases-popup' : '',
            })
              .setLngLat([point.lon, point.lat])
              .setHTML(popupContentString)
              .addTo(map)
          }
        })
      }
    })
  }, [pointsToDraw, mapLoaded])

  return <div id={id} ref={mapContainerRef} style={containerStyle} />
}

export default MapBoxWithControls
